import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfSale = () => {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center text-red-600 hover:text-red-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Sale</h1>
        <p className="text-gray-600 mb-8">Last updated: 18th January 2026</p>
        
        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              These Terms of Sale govern your purchase of digital products and subscription services from 30 Day Skills ("we", "our", "us") through our website 30dayskills.co.uk (the "Site"). By completing a purchase, you agree to these terms.
            </p>
            <p className="text-gray-700 mb-4">
              Please read these terms carefully before making a purchase. These terms apply in addition to our Website Terms of Use and Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Products and Services</h2>
            <p className="text-gray-700 mb-4">We offer the following types of products and services:</p>
            
            <h3 className="text-lg font-medium text-gray-900 mb-3">2.1 Digital Books (Instant Delivery)</h3>
            <p className="text-gray-700 mb-4">
              Downloadable digital books in PDF and/or ePub format. Upon successful payment, you will receive immediate access to download links via email and through your account (if applicable). Download links remain active for a limited period and have a maximum number of downloads.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-3">2.2 Pre-Release Books (Delayed Delivery)</h3>
            <p className="text-gray-700 mb-4">
              Books available for pre-order before their official release date. Payment is taken at the time of order. Delivery will occur on or around the stated release date. You will be notified by email when your pre-ordered item is available for download.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-3">2.3 Subscription Services</h3>
            <p className="text-gray-700 mb-4">
              Access to the Managerial Thinking Companion and/or other subscription-based services. Subscription terms, including duration and renewal, are specified at the time of purchase. Separate terms of service apply to specific subscription products.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Pricing and Payment</h2>
            <p className="text-gray-700 mb-4">
              All prices are displayed in GBP (British Pounds) and include VAT where applicable. We reserve the right to change prices at any time, but changes will not affect orders already placed.
            </p>
            <p className="text-gray-700 mb-4">
              Payment is processed securely through Stripe. We accept major credit and debit cards. Your payment information is transmitted directly to Stripe and is not stored on our servers.
            </p>
            <p className="text-gray-700 mb-4">
              For subscriptions, your payment method will be charged automatically at the beginning of each billing period unless you cancel before the renewal date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Delivery</h2>
            
            <h3 className="text-lg font-medium text-gray-900 mb-3">4.1 Digital Books (Instant)</h3>
            <p className="text-gray-700 mb-4">
              Delivery is electronic. You will receive an email containing download link(s) within minutes of successful payment. If you do not receive the email, please check your spam folder and contact us at support@30dayskills.co.uk.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-3">4.2 Pre-Release Books</h3>
            <p className="text-gray-700 mb-4">
              Delivery will occur on or around the stated release date. We will notify you by email when your content is available. Release dates are estimates and may change. If a release date changes significantly, we will notify you and offer the option to cancel for a full refund.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-3">4.3 Subscription Access</h3>
            <p className="text-gray-700 mb-4">
              Access begins immediately upon successful payment (or at the start of a trial period, if applicable). Access instructions will be sent to the email address associated with your purchase.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Refunds and Cancellations</h2>
            
            <h3 className="text-lg font-medium text-gray-900 mb-3">5.1 Digital Books (Instant Delivery)</h3>
            <p className="text-gray-700 mb-4">
              Due to the nature of digital products, we do not generally offer refunds once download links have been accessed. However, if you experience technical issues that prevent you from accessing your purchase, please contact us within 14 days of purchase and we will work to resolve the issue or provide a refund.
            </p>
            <p className="text-gray-700 mb-4">
              Under the Consumer Contracts Regulations 2013, you have the right to cancel your order within 14 days of purchase. However, by proceeding with a digital download, you acknowledge that you are requesting immediate performance of the contract and waive your right to cancel once the download has begun.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-3">5.2 Pre-Release Books</h3>
            <p className="text-gray-700 mb-4">
              You may cancel a pre-order at any time before the release date for a full refund. To cancel, contact us at support@30dayskills.co.uk with your order details.
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-3">5.3 Subscriptions</h3>
            <p className="text-gray-700 mb-4">
              You may cancel your subscription at any time. Cancellation will take effect at the end of your current billing period. You will retain access until the end of the period you have paid for.
            </p>
            <p className="text-gray-700 mb-4">
              We do not provide prorated refunds for partial billing periods. If you believe you are entitled to a refund due to service issues, please contact us at support@30dayskills.co.uk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Access Duration</h2>
            <p className="text-gray-700 mb-4">
              <strong>Digital Books:</strong> Once downloaded, the files are yours to keep. Download links remain active for 30 days from purchase with a maximum of 5 downloads.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Subscriptions:</strong> Access continues for the duration of your active subscription. Upon cancellation or non-renewal, access to subscription features will end at the conclusion of your paid period.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Licence and Permitted Use</h2>
            <p className="text-gray-700 mb-4">
              When you purchase a digital product, you are granted a personal, non-exclusive, non-transferable licence to use the content for your own personal, non-commercial purposes. You may not:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Share, distribute, or resell the content.</li>
              <li>Upload the content to file-sharing services.</li>
              <li>Remove or alter any copyright notices.</li>
              <li>Use the content for commercial purposes without written permission.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Liability</h2>
            <p className="text-gray-700 mb-4">
              Our digital products and services are provided for informational and educational purposes only. While we strive to provide high-quality content, we make no guarantees regarding specific outcomes from using our products.
            </p>
            <p className="text-gray-700 mb-4">
              To the extent permitted by law, our liability for any claims arising from your purchase is limited to the amount you paid for the product or service in question.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms of Sale are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
            <p className="text-gray-700 mb-4">
              Nothing in these terms affects your statutory rights as a consumer under applicable UK consumer protection legislation.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For questions about purchases, refunds, or these Terms of Sale, please contact us:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> support@30dayskills.co.uk<br />
              <strong>Website:</strong> 30dayskills.co.uk
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfSale;
